import { logger } from "../../utils/logger";
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { Plus, Edit2, Trash2, X, Calendar, Clock } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyEvent = {
  event_name: "",
  day: "Monday",
  date: "",
  buy_in: "",
  chips: "",
  staff_bonus: "",
  add_ons: "",
  guarantee: "",
  start_time: "",
  description: "",
  location: "San Marcos Social Club",
};

const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function EventsManager() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState(emptyEvent);
  const { getAuthHeaders } = useAuth();

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events/all`, getAuthHeaders());
      setEvents(response.data);
    } catch (err) {
      logger.error("Error fetching events:", err);
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const openCreateDialog = () => {
    setEditingEvent(null);
    setFormData(emptyEvent);
    setDialogOpen(true);
  };

  const openEditDialog = (event) => {
    setEditingEvent(event);
    setFormData({
      event_name: event.event_name || "",
      day: event.day || "Monday",
      date: event.date || "",
      buy_in: event.buy_in || "",
      chips: event.chips || "",
      staff_bonus: event.staff_bonus || "",
      add_ons: event.add_ons || "",
      guarantee: event.guarantee || "",
      start_time: event.start_time || "",
      description: event.description || "",
      location: event.location || "San Marcos Social Club",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.event_name || !formData.day || !formData.start_time) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      if (editingEvent) {
        await axios.put(`${API}/events/${editingEvent.id}`, formData, getAuthHeaders());
        toast.success("Event updated!");
      } else {
        await axios.post(`${API}/events`, formData, getAuthHeaders());
        toast.success("Event created!");
      }
      setDialogOpen(false);
      fetchEvents();
    } catch (err) {
      toast.error("Failed to save event");
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm("Are you sure you want to delete this event?")) return;
    
    try {
      await axios.delete(`${API}/events/${eventId}`, getAuthHeaders());
      toast.success("Event deleted!");
      fetchEvents();
    } catch (err) {
      toast.error("Failed to delete event");
    }
  };

  if (loading) {
    return <div className="text-gold animate-pulse">Loading events...</div>;
  }

  return (
    <div data-testid="events-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl text-white mb-2">Events Manager</h1>
          <p className="text-white/60">Manage your tournament schedule and events</p>
        </div>
        <Button
          onClick={openCreateDialog}
          className="bg-gold text-black hover:bg-gold-dark"
          data-testid="add-event-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Event
        </Button>
      </div>

      {/* Events List */}
      <div className="grid gap-4">
        {events.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <Calendar className="w-12 h-12 text-gold/30 mx-auto mb-4" />
            <p className="text-white/60">No events yet. Create your first event!</p>
          </div>
        ) : (
          events.map((event) => (
            <div
              key={event.id}
              className="glass-card p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
              data-testid={`event-item-${event.id}`}
            >
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="px-3 py-1 bg-gold/20 text-gold text-xs uppercase tracking-wider rounded">
                    {event.day}
                  </span>
                  <span className="text-white/50 text-sm flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {event.start_time}
                  </span>
                </div>
                <h3 className="font-heading text-xl text-white mb-1">{event.event_name}</h3>
                {event.buy_in && <p className="text-white/60 text-sm">{event.buy_in}</p>}
                {event.guarantee && <p className="text-gold text-sm">{event.guarantee}</p>}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openEditDialog(event)}
                  className="border-white/20 text-white hover:border-gold hover:text-gold"
                  data-testid={`edit-event-${event.id}`}
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDelete(event.id)}
                  className="border-white/20 text-white hover:border-red-500 hover:text-red-500"
                  data-testid={`delete-event-${event.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-casino-dark border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {editingEvent ? "Edit Event" : "Create Event"}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="event-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-white/80">Event Name *</Label>
                <Input
                  value={formData.event_name}
                  onChange={(e) => setFormData({ ...formData, event_name: e.target.value })}
                  className="bg-casino-secondary border-white/10 text-white mt-1"
                  placeholder="e.g., Turn Up Tuesday"
                  data-testid="event-name-input"
                />
              </div>
              <div>
                <Label className="text-white/80">Day *</Label>
                <select
                  value={formData.day}
                  onChange={(e) => setFormData({ ...formData, day: e.target.value })}
                  className="w-full h-10 bg-casino-secondary border border-white/10 rounded-md text-white px-3 mt-1"
                  data-testid="event-day-select"
                >
                  {days.map((day) => (
                    <option key={day} value={day}>{day}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-white/80">Start Time *</Label>
                <Input
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  className="bg-casino-secondary border-white/10 text-white mt-1"
                  placeholder="e.g., 6 PM"
                  data-testid="event-time-input"
                />
              </div>
              <div>
                <Label className="text-white/80">Date (optional)</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="bg-casino-secondary border-white/10 text-white mt-1"
                  data-testid="event-date-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-white/80">Buy-In</Label>
                <Input
                  value={formData.buy_in}
                  onChange={(e) => setFormData({ ...formData, buy_in: e.target.value })}
                  className="bg-casino-secondary border-white/10 text-white mt-1"
                  placeholder="e.g., $50"
                  data-testid="event-buyin-input"
                />
              </div>
              <div>
                <Label className="text-white/80">Chips</Label>
                <Input
                  value={formData.chips}
                  onChange={(e) => setFormData({ ...formData, chips: e.target.value })}
                  className="bg-casino-secondary border-white/10 text-white mt-1"
                  placeholder="e.g., 60k chips"
                  data-testid="event-chips-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-white/80">Staff Bonus</Label>
                <Input
                  value={formData.staff_bonus}
                  onChange={(e) => setFormData({ ...formData, staff_bonus: e.target.value })}
                  className="bg-casino-secondary border-white/10 text-white mt-1"
                  placeholder="e.g., $10 staff bonus - 40k chips"
                  data-testid="event-bonus-input"
                />
              </div>
              <div>
                <Label className="text-white/80">Add-Ons</Label>
                <Input
                  value={formData.add_ons}
                  onChange={(e) => setFormData({ ...formData, add_ons: e.target.value })}
                  className="bg-casino-secondary border-white/10 text-white mt-1"
                  placeholder="e.g., Up to 5 $20 add-ons"
                  data-testid="event-addons-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-white/80">Guarantee</Label>
              <Input
                value={formData.guarantee}
                onChange={(e) => setFormData({ ...formData, guarantee: e.target.value })}
                className="bg-casino-secondary border-white/10 text-white mt-1"
                placeholder="e.g., $500 Guarantee"
                data-testid="event-guarantee-input"
              />
            </div>

            <div>
              <Label className="text-white/80">Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-casino-secondary border-white/10 text-white mt-1 min-h-[80px]"
                placeholder="Additional details about this event..."
                data-testid="event-description-input"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="flex-1 border-white/20 text-white"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gold text-black hover:bg-gold-dark"
                data-testid="event-submit-btn"
              >
                {editingEvent ? "Update Event" : "Create Event"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
